import { analytics } from '@features/analytics';
import { RateOrPackageDetail } from '@features/packages/pages/Package';
import { StaticRate, getAvailableRate, getStaticRate, ratesKeys } from '@features/rates';
import { shouldNoindexRate } from '@features/rates/helpers/shouldNoindexRate.helper';
import { getRoomSettings, roomSettingsKeys } from '@features/roomSettings';
import { PagePropsWithSSR } from '@features/state';
import { NormalizedStore } from '@store/entity/entity.types';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { parseDateToString } from '@utils/helpers';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import { useEffect } from 'react';
import { Router } from 'shared-routing';
import { Property } from 'shared-types/Property';

export type RatePageProps = {
  id: string;
  property: Property;
  language: string;
  rate: StaticRate;
  url: string;
};

const RatePage: NextPage<RatePageProps> = ({ property, language, rate, url }) => {
  const rateTitle = rate.name ?? '';
  const { propertyTitle } = property;

  const pageTitle = `${rateTitle} ${propertyTitle ? `| ${propertyTitle}` : ''}`;

  useEffect(() => {
    analytics.track({
      event: 'view_item',
      params: {
        items: [{ item_category: 'rates', item_id: rate.rateCode, item_name: rate.name ?? '' }],
      },
    });
  }, []);

  const shouldNoindex = shouldNoindexRate(rate);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          noindex: shouldNoindex,
          openGraph: {
            title: pageTitle,
            description: rate.description ?? '',
            locale: language,
            url,
            images: [{ url: rate.images?.[0] ?? '' }],
          },
        }}
      />
      <RateOrPackageDetail property={property} language={language} />
    </>
  );
};

type RateInitialProps = {
  store: NormalizedStore;
} & NextPageContext &
  Pick<RatePageProps, 'language' | 'url' | 'property'>;

RatePage.getInitialProps = async (
  props: RateInitialProps
): Promise<PagePropsWithSSR<RatePageProps>> => {
  const { store, property, language, url, res, query, moduleStatuses } = props;
  const { slug: siteSlug, id: siteId } = property;

  if (!moduleStatuses.roomsEnabled) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }

  const state = store.getState();
  const journeyParams = yourJourneySelectors.getParsedJourneyParams(state);

  const adults = journeyParams.adults;
  const children = journeyParams.children;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const promoRateCode = typeof query.promoRateCode === 'string' ? query.promoRateCode : undefined;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const rateCode = query.id as string;
  const client = new QueryClient();
  const { data: rate } = await client.fetchQuery({
    queryKey: ratesKeys.staticRate({ siteSlug, rateCode, language }),
    queryFn: () => getStaticRate({ siteSlug, rateCode, language }),
  });
  await client.prefetchQuery({
    queryKey: ratesKeys.availableRate(
      rateCode,
      {
        siteId,
        adults,
        children,
        childAges,
        fromDate,
        toDate,
        promoRateCode,
        rooms,
      },
      rooms.length > 1
    ),
    queryFn: () =>
      getAvailableRate(
        rateCode,
        {
          siteId,
          adults,
          children,
          childAges,
          fromDate,
          toDate,
          promoRateCode,
          rooms,
        },
        rooms.length > 1
      ),
  });
  await client.prefetchQuery({
    queryKey: roomSettingsKeys.roomSettings({ siteSlug }),
    queryFn: () => getRoomSettings({ siteSlug }),
  });

  return {
    id: rateCode,
    property,
    language,
    rate,
    url,
    dehydratedState: dehydrate(client),
  };
};

export default RatePage;
